import type { UIMessage } from "@ai-sdk/react";
import type { UIDataTypes, UIMessagePart } from "ai";
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { SearchHit } from "../types";

import { postAgentStudioFeedback, postFeedback } from "./askai";

import {
  isAskAiPromptBlockingError,
  promptBlockingBannerMessage,
  showAskAiBlockingBannerNewConversationLink,
  ThreadDepthErrorBanner,
} from "./error-utils";
import {
  BrainIcon,
  CheckIcon,
  CopyIcon,
  DislikeIcon,
  LikeIcon,
  SearchIcon,
} from "./icons";
import { MemoizedMarkdown } from "./markdown";
import type { SuggestedQuestionHit } from "./use-suggested-questions";

function useClipboard() {
  const copyText = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Silently fail - clipboard access might be blocked
    }
  }, []);

  return { copyText };
}

// keyboard listener not needed: input handled in parent

interface ChatWidgetProps {
  messages: Message[];
  error: Error | null;
  isGenerating: boolean;
  onCopy?: (text: string) => Promise<void> | void;
  onThumbsUp?: (userMessageId: string) => Promise<void> | void;
  onThumbsDown?: (userMessageId: string) => Promise<void> | void;
  applicationId: string;
  apiKey?: string;
  assistantId: string;
  agentStudio?: boolean;
  suggestedQuestions?: SuggestedQuestionHit[];
  showPromptBlockingError?: boolean;
  threadDepthBannerInChat?: boolean;
  showAiDisclaimer?: boolean;
  newestExchangeFirst?: boolean;
  onSuggestedQuestionClick?: (question: string) => void;
  onNewChat?: () => void;
}

export interface SearchIndexTool {
  input: {
    query: string;
  };
  output: {
    query: string;
    hits: SearchHit[];
  };
}

export type Message = UIMessage<
  unknown,
  UIDataTypes,
  {
    searchIndex: SearchIndexTool;
  }
>;

export type AIMessagePart = UIMessagePart<
  UIDataTypes,
  {
    searchIndex: SearchIndexTool;
  }
>;

interface Exchange {
  id: string;
  userMessage: Message;
  assistantMessage: Message | null;
}

export const ChatWidget = memo(function ChatWidget({
  messages,
  error,
  isGenerating,
  onCopy,
  onThumbsUp,
  onThumbsDown,
  applicationId,
  apiKey,
  assistantId,
  agentStudio,
  suggestedQuestions,
  onSuggestedQuestionClick,
  onNewChat,
  showPromptBlockingError = false,
  threadDepthBannerInChat = true,
  showAiDisclaimer = true,
  newestExchangeFirst = true,
}: ChatWidgetProps) {
  const { copyText } = useClipboard();
  const chatScrollRef = useRef<HTMLDivElement>(null);
  /** When false, user has scrolled away from the bottom; avoid snapping on updates. */
  const stickChatToBottomRef = useRef(true);
  const [copiedExchangeId, setCopiedExchangeId] = useState<string | null>(null);
  const copyResetTimeoutRef = useRef<number | null>(null);
  const [acknowledgedExchangeIds, setAcknowledgedExchangeIds] = useState<
    Set<string>
  >(new Set());
  const [submittingExchangeId, setSubmittingExchangeId] = useState<
    string | null
  >(null);

  const handleFeedback = async (exchange: Exchange, vote: 0 | 1) => {
    if (!exchange.assistantMessage) return;
    const customHandler = vote === 1 ? onThumbsUp : onThumbsDown;
    try {
      setSubmittingExchangeId(exchange.id);
      if (customHandler) {
        await customHandler(exchange.userMessage.id);
      } else if (agentStudio) {
        if (apiKey) {
          await postAgentStudioFeedback({
            agentId: assistantId,
            vote,
            messageId: exchange.assistantMessage.id,
            appId: applicationId,
            apiKey,
          });
        }
      } else {
        await postFeedback({
          assistantId,
          appId: applicationId,
          messageId: exchange.userMessage.id,
          thumbs: vote,
        });
      }
      setAcknowledgedExchangeIds((prev) => {
        const next = new Set(prev);
        next.add(exchange.id);
        return next;
      });
    } catch {
      // ignore errors
    } finally {
      setSubmittingExchangeId(null);
    }
  };

  // Group messages into exchanges (user + assistant pairs)
  const exchanges = useMemo(() => {
    const grouped: Exchange[] = [];

    for (let i = 0; i < messages.length; i++) {
      const current = messages.at(i);
      if (!current) {
        continue;
      }
      if (current.role === "user") {
        const userMessage = current as Message;
        const nextMessage = messages.at(i + 1);
        if (nextMessage?.role === "assistant") {
          grouped.push({
            id: userMessage.id,
            userMessage,
            assistantMessage: nextMessage as Message,
          });
          i++; // Skip the assistant message since we've already processed it
        } else {
          // No assistant yet – show a pending exchange immediately
          grouped.push({
            id: userMessage.id,
            userMessage,
            assistantMessage: null,
          });
        }
      }
    }
    return grouped;
  }, [messages]);

  const orderedExchanges = useMemo(
    () => (newestExchangeFirst ? [...exchanges].reverse() : exchanges),
    [exchanges, newestExchangeFirst],
  );

  const updateStickToBottomFromScroll = useCallback(() => {
    if (newestExchangeFirst) return;
    const root = chatScrollRef.current;
    if (!root) return;
    const thresholdPx = 80;
    const dist = root.scrollHeight - root.scrollTop - root.clientHeight;
    stickChatToBottomRef.current = dist < thresholdPx;
  }, [newestExchangeFirst]);

  // Scroll after DOM updates when transcript or generation state changes (not only when exchange count changes).
  // biome-ignore lint/correctness/useExhaustiveDependencies: messages / isGenerating intentionally trigger stick-to-bottom
  useLayoutEffect(() => {
    if (newestExchangeFirst) return;
    const root = chatScrollRef.current;
    if (!root) return;
    if (stickChatToBottomRef.current) {
      root.scrollTop = root.scrollHeight;
    }
    updateStickToBottomFromScroll();
  }, [
    newestExchangeFirst,
    messages,
    isGenerating,
    orderedExchanges.length,
    updateStickToBottomFromScroll,
  ]);

  // Cleanup any pending reset timers on unmount
  useEffect(() => {
    return () => {
      if (copyResetTimeoutRef.current) {
        window.clearTimeout(copyResetTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      className="ss-chat-root"
      ref={chatScrollRef}
      onScroll={updateStickToBottomFromScroll}
    >
      <div className="ss-qa-list">
        {exchanges.length === 0 ? (
          <div className="ss-chat-welcome">
            <h2 className="ss-chat-welcome-title">How can I help you today?</h2>
            <p className="ss-chat-welcome-subtitle">
              I search through your content to help you find answers to your
              questions, fast.
            </p>
            {suggestedQuestions && suggestedQuestions.length > 0 ? (
              <div className="ss-suggested-questions">
                {suggestedQuestions.map((question) => (
                  <button
                    key={question.objectID}
                    type="button"
                    className="ss-suggested-question-btn"
                    disabled={isGenerating}
                    onClick={() => {
                      if (isGenerating) return;
                      onSuggestedQuestionClick?.(question.question);
                    }}
                  >
                    {question.question}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
        {threadDepthBannerInChat && showPromptBlockingError ? (
          <ThreadDepthErrorBanner
            onNewChat={onNewChat}
            detailMessage={promptBlockingBannerMessage(error, agentStudio)}
            showNewConversationLink={showAskAiBlockingBannerNewConversationLink(
              error,
              agentStudio,
            )}
          />
        ) : null}
        {showAiDisclaimer ? (
          <p className="ss-hint">
            Answers are generated with AI which can make mistakes.
          </p>
        ) : null}
        {/* other errors - never show AI-217 in the generic banner */}
        {error && !isAskAiPromptBlockingError(error, Boolean(agentStudio)) && (
          <div className="ss-error-banner">{error.message}</div>
        )}

        {/* exchanges */}
        {orderedExchanges.map((exchange, index) => {
          const isLastExchange = newestExchangeFirst
            ? index === 0
            : index === orderedExchanges.length - 1;

          return (
            <article key={exchange.id} className="ss-qa-card">
              <div className="ss-qa-header">
                <div className="ss-qa-question">
                  {exchange.userMessage.parts.map((part, index) =>
                    part.type === "text" ? (
                      // biome-ignore lint/suspicious/noArrayIndexKey: better
                      <span key={index}>
                        {typeof part.text === "string" ? part.text : ""}
                      </span>
                    ) : null,
                  )}
                </div>
              </div>

              <div className="ss-qa-answer">
                <div className="ss-qa-answer-content">
                  {exchange.assistantMessage ? (
                    <div className="ss-qa-markdown">
                      {exchange.assistantMessage.parts.map((part, index) => {
                        if (typeof part === "string") {
                          // biome-ignore lint/suspicious/noArrayIndexKey: better
                          return <p key={`${index}`}>{part}</p>;
                        }
                        if (part.type === "text") {
                          const text =
                            typeof part.text === "string" ? part.text : "";
                          return (
                            // biome-ignore lint/suspicious/noArrayIndexKey: better
                            <MemoizedMarkdown key={`${index}`}>
                              {text}
                            </MemoizedMarkdown>
                          );
                        } else if (
                          part.type === "reasoning" &&
                          part.state === "streaming"
                        ) {
                          return (
                            // biome-ignore lint/suspicious/noArrayIndexKey: better
                            <p className="ss-tool-info" key={`${index}`}>
                              <BrainIcon />{" "}
                              <span className="ss-shimmer-text">
                                Reasoning...
                              </span>
                            </p>
                          );
                        } else if (part.type === "tool-searchIndex") {
                          if (part.state === "input-streaming") {
                            return (
                              // biome-ignore lint/suspicious/noArrayIndexKey: better
                              <p className="ss-tool-info" key={`${index}`}>
                                <SearchIcon size={18} />{" "}
                                <span className="ss-shimmer-text">
                                  Searching...
                                </span>
                              </p>
                            );
                          } else if (part.state === "input-available") {
                            return (
                              // biome-ignore lint/suspicious/noArrayIndexKey: better
                              <p className="ss-tool-info" key={`${index}`}>
                                <SearchIcon size={18} />{" "}
                                <span className="ss-shimmer-text">
                                  Looking for{" "}
                                  <mark>
                                    &quot;{part.input?.query || ""}&quot;
                                  </mark>
                                </span>
                              </p>
                            );
                          } else if (part.state === "output-available") {
                            return (
                              // biome-ignore lint/suspicious/noArrayIndexKey: better
                              <p className="ss-tool-info" key={`${index}`}>
                                <SearchIcon size={18} />{" "}
                                <span>
                                  Searched for{" "}
                                  <mark>&quot;{part.output?.query}&quot;</mark>{" "}
                                  found{" "}
                                  {Array.isArray(part.output?.hits)
                                    ? part.output.hits.length
                                    : "no"}{" "}
                                  results
                                </span>
                              </p>
                            );
                          } else if (part.state === "output-error") {
                            return (
                              // biome-ignore lint/suspicious/noArrayIndexKey: better
                              <p className="ss-tool-info" key={`${index}`}>
                                {part.errorText}
                              </p>
                            );
                          } else {
                            return null;
                          }
                        } else {
                          return null;
                        }
                      })}
                    </div>
                  ) : (
                    <div className="ss-qa-markdown ss-qa-generating ss-shimmer-text">
                      {isGenerating && isLastExchange ? "Thinking..." : ""}
                    </div>
                  )}
                </div>
              </div>

              <div className="ss-qa-actions">
                {exchange.assistantMessage && !isGenerating ? (
                  acknowledgedExchangeIds.has(exchange.id) ? (
                    <span className="ss-qa-feedback-ack ss-fade">
                      Thanks for your feedback!
                    </span>
                  ) : submittingExchangeId === exchange.id ? (
                    <span className="ss-qa-feedback-ack ss-shimmer-text">
                      Submitting...
                    </span>
                  ) : (
                    <div className="ss-qa-actions-group">
                      <button
                        type="button"
                        title="Like"
                        aria-label="Like"
                        className="ss-qa-action-btn"
                        disabled={
                          !exchange.assistantMessage ||
                          submittingExchangeId === exchange.id
                        }
                        onClick={() => handleFeedback(exchange, 1)}
                      >
                        <LikeIcon size={18} />
                      </button>
                      <button
                        type="button"
                        title="Dislike"
                        aria-label="Dislike"
                        className="ss-qa-action-btn"
                        disabled={
                          !exchange.assistantMessage ||
                          submittingExchangeId === exchange.id
                        }
                        onClick={() => handleFeedback(exchange, 0)}
                      >
                        <DislikeIcon size={18} />
                      </button>
                    </div>
                  )
                ) : null}
                <button
                  type="button"
                  className={`ss-qa-action-btn ${
                    copiedExchangeId === exchange.id ? "is-copied" : ""
                  }`}
                  aria-label={
                    copiedExchangeId === exchange.id ? "Copied" : "Copy answer"
                  }
                  title={
                    copiedExchangeId === exchange.id ? "Copied" : "Copy answer"
                  }
                  disabled={
                    !exchange.assistantMessage ||
                    copiedExchangeId === exchange.id
                  }
                  onClick={async () => {
                    const parts = exchange.assistantMessage?.parts ?? [];
                    const textContent = parts
                      .flatMap((part) =>
                        part.type === "text" && typeof part.text === "string"
                          ? [part.text]
                          : [],
                      )
                      .join("")
                      .trim();
                    if (!textContent) return;
                    try {
                      if (onCopy) {
                        await onCopy(textContent);
                      } else {
                        await copyText(textContent);
                      }
                      setCopiedExchangeId(exchange.id);
                      if (copyResetTimeoutRef.current) {
                        window.clearTimeout(copyResetTimeoutRef.current);
                      }
                      copyResetTimeoutRef.current = window.setTimeout(() => {
                        setCopiedExchangeId(null);
                      }, 1500);
                    } catch {
                      // noop – copy may fail silently
                    }
                  }}
                >
                  {copiedExchangeId === exchange.id ? (
                    <CheckIcon size={18} />
                  ) : (
                    <CopyIcon size={18} />
                  )}
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
});
